<template>
  <div
    ref="wrapRef"
    class="gender-lw-chart flex min-h-0 min-w-0 w-full flex-col rounded-2xl border border-gray-modern-100 bg-white p-6 shadow-sm"
    role="region"
    :aria-label="ariaLabel"
  >
    <div class="min-w-0 shrink">
      <div class="flex min-w-0 items-start justify-between gap-3">
        <div class="min-w-0 flex-1 flex flex-col gap-2">
          <h3 class="gender-lw-chart__title">{{ title }}</h3>
          <p class="gender-lw-chart__subtitle">{{ subtitle }}</p>
        </div>
        <ChartImageDownloadButton
          v-if="downloadable"
          :disabled="!canDownload"
          :busy="isDownloading"
          @click="downloadImage"
        />
      </div>

      <div class="min-w-0 lg:pl-8">
        <div
          class="mt-4 mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3"
        >
          <div
            class="gender-lw-chart__toggle"
            role="radiogroup"
            :aria-label="toggleGroupLabel"
          >
            <button
              type="button"
              role="radio"
              :aria-checked="viewMode === 'count'"
              class="gender-lw-chart__toggle-btn"
              :class="
                viewMode === 'count'
                  ? 'gender-lw-chart__toggle-btn--active'
                  : ''
              "
              @click="viewMode = 'count'"
            >
              {{ labelCount }}
            </button>
            <button
              type="button"
              role="radio"
              :aria-checked="viewMode === 'pct'"
              class="gender-lw-chart__toggle-btn"
              :class="
                viewMode === 'pct' ? 'gender-lw-chart__toggle-btn--active' : ''
              "
              @click="viewMode = 'pct'"
            >
              {{ labelPct }}
            </button>
          </div>

          <ul
            class="gender-lw-chart__legend m-0 flex shrink-0 list-none flex-row flex-nowrap items-center gap-6 p-0"
          >
            <li class="gender-lw-chart__legend-item">
              <span
                class="gender-lw-chart__legend-dot"
                :style="{ background: colorWomen }"
              />
              {{ legendWomen }}
            </li>
            <li class="gender-lw-chart__legend-item">
              <span
                class="gender-lw-chart__legend-dot"
                :style="{ background: colorMen }"
              />
              {{ legendMen }}
            </li>
          </ul>
        </div>

        <div
          ref="chartRef"
          data-chart-scroll-plot
          class="gender-lw-chart__plot mt-8 min-h-0 min-w-0 w-full shrink-0 overflow-x-auto [-webkit-overflow-scrolling:touch]"
        >
          <svg
            class="gender-lw-chart__svg block h-auto max-w-none shrink-0"
            :width="svgW"
            :height="svgH"
            :viewBox="`0 0 ${svgW} ${svgH}`"
            preserveAspectRatio="xMinYMid meet"
            aria-hidden="true"
          >
            <!-- Horizontal grid -->
            <g v-for="(tick, ti) in yTicksLayout" :key="'g' + ti">
              <line
                class="gender-lw-chart__grid-line"
                :x1="plot.left"
                :y1="tick.y"
                :x2="plot.right"
                :y2="tick.y"
              />
              <text
                class="gender-lw-chart__y-label"
                :x="plot.left - 8"
                :y="tick.y + 4"
                text-anchor="end"
              >
                {{ tick.label }}
              </text>
            </g>

            <!-- Bars -->
            <g v-for="(g, gi) in barLayout.groups" :key="'b' + gi">
              <path v-if="g.womenH > 0" :d="g.womenPath" :fill="colorWomen" />
              <path v-if="g.menH > 0" :d="g.menPath" :fill="colorMen" />
              <text
                class="gender-lw-chart__x-label"
                :x="g.centerX"
                :y="plot.baseY + 20"
                text-anchor="middle"
              >
                {{ g.label }}
              </text>
            </g>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import ChartImageDownloadButton from "./ChartImageDownloadButton.vue";
import { useChartImageDownload } from "../utils/useChartImageDownload";

const COLOR_WOMEN = "var(--Teal-400, #2ED3B7)";
const COLOR_MEN = "var(--Indigo-500, #6172F3)";

/** Vertical bars: flat bottom, rounded top (both top corners). */
function verticalBarPathTopRound(left, topY, width, height, radius) {
  if (!(width > 0) || !(height > 0)) return "";
  const r = Math.min(radius, width / 2, height / 2);
  const x = left;
  const yBottom = topY + height;
  if (r < 0.5) {
    return `M ${x} ${topY} L ${x + width} ${topY} L ${x + width} ${yBottom} L ${x} ${yBottom} Z`;
  }
  return [
    `M ${x} ${yBottom}`,
    `L ${x} ${topY + r}`,
    `A ${r} ${r} 0 0 1 ${x + r} ${topY}`,
    `L ${x + width - r} ${topY}`,
    `A ${r} ${r} 0 0 1 ${x + width} ${topY + r}`,
    `L ${x + width} ${yBottom}`,
    "Z",
  ].join(" ");
}

/** Mock pairs — design placeholder (not wired to backend yet). */
const MOCK_ROWS = [
  {
    country: "Bangladesh",
    womenCount: 980,
    menCount: 420,
    womenPct: 78,
    menPct: 41,
  },
  {
    country: "India",
    womenCount: 840,
    menCount: 358,
    womenPct: 69,
    menPct: 34,
  },
  {
    country: "Kenya",
    womenCount: 720,
    menCount: 305,
    womenPct: 62,
    menPct: 30,
  },
  {
    country: "Brazil",
    womenCount: 600,
    menCount: 258,
    womenPct: 54,
    menPct: 26,
  },
  {
    country: "Vietnam",
    womenCount: 520,
    menCount: 220,
    womenPct: 48,
    menPct: 23,
  },
  {
    country: "Sri Lanka",
    womenCount: 445,
    menCount: 188,
    womenPct: 41,
    menPct: 19,
  },
  {
    country: "Indonesia",
    womenCount: 380,
    menCount: 160,
    womenPct: 36,
    menPct: 17,
  },
  {
    country: "Ghana",
    womenCount: 290,
    menCount: 122,
    womenPct: 28,
    menPct: 13,
  },
];

const props = defineProps({
  title: {
    type: String,
    default: "Workers below living wage by gender",
  },
  subtitle: {
    type: String,
    default: "Per country, grouped by gender",
  },
  labelCount: { type: String, default: "Count" },
  labelPct: { type: String, default: "% of Workforce" },
  legendWomen: { type: String, default: "Women below LW" },
  legendMen: { type: String, default: "Men below LW" },
  toggleGroupLabel: { type: String, default: "Chart value type" },
  /** JSON array: { country, womenCount, menCount, womenPct?, menPct? }[] */
  rowsJson: { type: String, default: "" },
  downloadable: { type: Boolean, default: false },
  downloadContext: { type: String, default: "" },
});

const viewMode = ref("count");

const colorWomen = COLOR_WOMEN;
const colorMen = COLOR_MEN;

const rowsFromServer = computed(() => {
  const s = props.rowsJson;
  if (typeof s !== "string" || !s.trim()) return undefined;
  try {
    const data = JSON.parse(s);
    return Array.isArray(data) ? data : undefined;
  } catch {
    return undefined;
  }
});

const resolvedRows = computed(() => {
  if (rowsFromServer.value !== undefined) return rowsFromServer.value;
  return MOCK_ROWS;
});

const hasChartData = computed(
  () =>
    Array.isArray(rowsFromServer.value) && rowsFromServer.value.length > 0,
);

function countAxisFromRows(rows) {
  let peak = 0;
  for (const r of rows) {
    peak = Math.max(peak, Number(r.womenCount) || 0, Number(r.menCount) || 0);
  }
  if (peak <= 0) {
    return { max: 100, tickStep: 20, format: (n) => String(Math.round(n)) };
  }
  const target = peak * 1.1;
  const rawStep = Math.ceil(target / 5);
  const pow = 10 ** Math.max(0, Math.floor(Math.log10(rawStep)));
  const tickStep = Math.max(1, Math.ceil(rawStep / pow) * pow);
  let max = tickStep * 5;
  if (max < peak) {
    max += tickStep;
  }
  return { max, tickStep, format: (n) => String(Math.round(n)) };
}

const scaleConfig = computed(() =>
  viewMode.value === "count"
    ? countAxisFromRows(resolvedRows.value)
    : { max: 100, tickStep: 20, format: (n) => `${Math.round(n)}%` },
);

const ariaLabel = computed(() => `${props.title}. ${props.subtitle}`);

const wrapRef = ref(null);
const chartRef = ref(null);
const measuredW = ref(640);

const { downloadImage, isDownloading, canDownload } = useChartImageDownload({
  getRootElement: () => wrapRef.value,
  title: () => props.title,
  downloadContext: () => props.downloadContext,
  hasData: hasChartData,
});

const margin = { top: 12, right: 8, bottom: 42, left: 44 };
const plotHeightPx = 220;

/** Minimum plot-inner width per country so bar pairs + labels stay readable; triggers horizontal scroll on narrow viewports. */
const MIN_INNER_PX_PER_COUNTRY = 72;

const minSvgWidthForData = computed(() => {
  const n = resolvedRows.value.length;
  if (n <= 0) return 320;
  return margin.left + margin.right + n * MIN_INNER_PX_PER_COUNTRY;
});

const svgW = computed(() =>
  Math.max(320, measuredW.value, minSvgWidthForData.value),
);

const svgH = computed(() => margin.top + plotHeightPx + margin.bottom);

const plot = computed(() => ({
  left: margin.left,
  right: svgW.value - margin.right,
  top: margin.top,
  innerH: plotHeightPx,
  baseY: margin.top + plotHeightPx,
}));

const yTicksLayout = computed(() => {
  const cfg = scaleConfig.value;
  const { innerH } = plot.value;
  const ticks = [];
  for (let v = 0; v <= cfg.max + 1e-6; v += cfg.tickStep) {
    const t = cfg.max > 0 ? 1 - v / cfg.max : 1;
    const y = plot.value.top + t * innerH;
    ticks.push({ y, label: cfg.format(v) });
  }
  return ticks;
});

function numOr0(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function valueFor(row, mode, sex) {
  if (mode === "count") {
    return sex === "w" ? numOr0(row.womenCount) : numOr0(row.menCount);
  }
  return sex === "w" ? numOr0(row.womenPct) : numOr0(row.menPct);
}

const barLayout = computed(() => {
  const { left, right, baseY, innerH } = plot.value;
  const cfg = scaleConfig.value;
  const rows = resolvedRows.value;
  if (!rows.length) {
    return { groups: [] };
  }
  const n = rows.length;
  const plotW = right - left;
  const groupW = plotW / n;
  const BAR_W_TARGET = 24;
  const BAR_GAP_TARGET = 5;
  const pairWidthTarget = BAR_W_TARGET * 2 + BAR_GAP_TARGET;
  const safeInner = Math.max(groupW - 4, 0);
  const fitScale =
    safeInner >= pairWidthTarget
      ? 1
      : safeInner > 0
        ? safeInner / pairWidthTarget
        : 0;
  const BAR_W = BAR_W_TARGET * fitScale;
  const BAR_GAP = BAR_GAP_TARGET * fitScale;
  const pairWidth = BAR_W + BAR_GAP + BAR_W;
  const cornerR = BAR_W > 0 ? Math.min(6, BAR_W * 0.35) : 0;

  const groups = rows.map((row, i) => {
    const centerX = left + groupW * i + groupW / 2;
    const xWomen = centerX - pairWidth / 2;
    const xMen = xWomen + BAR_W + BAR_GAP;

    const vW = valueFor(row, viewMode.value, "w");
    const vM = valueFor(row, viewMode.value, "m");

    const hW = cfg.max > 0 ? Math.max(0, (vW / cfg.max) * innerH) : 0;
    const hM = cfg.max > 0 ? Math.max(0, (vM / cfg.max) * innerH) : 0;

    const topWY = baseY - hW;
    const topMY = baseY - hM;

    return {
      label: row.country,
      centerX,
      womenH: hW,
      menH: hM,
      womenPath: verticalBarPathTopRound(xWomen, topWY, BAR_W, hW, cornerR),
      menPath: verticalBarPathTopRound(xMen, topMY, BAR_W, hM, cornerR),
    };
  });

  return { groups };
});

let ro;

onMounted(() => {
  const measure = () => {
    const cw = chartRef.value?.clientWidth;
    const ww = wrapRef.value?.clientWidth;
    const wd = cw && cw > 0 ? cw : ww && ww > 0 ? ww : 640;
    measuredW.value = Math.floor(wd);
  };
  nextTick(() => {
    measure();
    ro = new ResizeObserver(measure);
    if (chartRef.value) ro.observe(chartRef.value);
  });
});

onBeforeUnmount(() => ro?.disconnect());
</script>

<style scoped>
.gender-lw-chart__title {
  margin: 0;
  color: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.gender-lw-chart__subtitle {
  margin: 0;
  color: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 2px;
}

.gender-lw-chart__toggle {
  display: inline-flex;
  align-items: stretch;
  gap: 0;
  padding: 0;
  border-radius: 8px;
  background: #f3f4f6;
}

.gender-lw-chart__toggle-btn {
  cursor: pointer;
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  border-radius: 0;
  border: 1px solid transparent;
  padding: 6px 12px;
  font-family: Inter, "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  color: #717680;
  background: transparent;
  box-shadow: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.gender-lw-chart__toggle-btn:first-child {
  border-radius: 8px 0 0 8px;
}

.gender-lw-chart__toggle-btn:last-child {
  border-radius: 0 8px 8px 0;
}

.gender-lw-chart__toggle-btn--active {
  border-radius: 8px;
  color: #414651;
  border: 1px solid #d5d7da;
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
}

.gender-lw-chart__toggle-btn:not(.gender-lw-chart__toggle-btn--active):hover {
  color: #636872;
}

.gender-lw-chart__legend {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 24px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.gender-lw-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  color: #6b7280;
  line-height: normal;
}

.gender-lw-chart__legend-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.gender-lw-chart__grid-line {
  stroke: #e5e7eb;
  stroke-width: 1;
  stroke-dasharray: 4 4;
}

.gender-lw-chart__y-label {
  fill: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
}

.gender-lw-chart__x-label {
  fill: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
}

.gender-lw-chart__toggle-btn:focus-visible {
  outline: 2px solid #265957;
  outline-offset: 2px;
}
</style>
